/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { isArray } from 'lodash';
import {
    Box,
    TextField,
    InputAdornment,
    Typography,
    Chip,
    CircularProgress,
    Avatar,
    alpha,
    IconButton,
    Popover,
    List,
    ListItemButton,
    ListItemText,
    Checkbox,
    Divider,
    Badge,
    Tooltip,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useSelector } from 'react-redux';
import { getLead } from 'src/redux/slices/analytics';
import { dispatch, useSelector as useReduxSelector } from 'src/redux/store';
import {
    getTablePreferences,
    handleTablePreferences,
    handleTablePreferencesEdit,
    setTablePreferenceLoading,
} from 'src/redux/slices/tablePreferences';
import { getCurrentTableData } from 'src/utils/tablePreferences';
import useGetCurrentUserId from 'src/hooks/useGetCurrentUserId';
import useReportExportFilename from 'src/hooks/useReportExportFilename';
import Iconify from 'src/components/Iconify';
import { DATE_TITLES } from 'src/constants/newLead';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(customParseFormat);
dayjs.extend(relativeTime);

// Status chip palette — kept in sync with TreatmentLeadPopup / SourceLeadPopup
// STATUS_COLORS so chips look identical across every table and popup.
// "Not Booked" stays red (user preference) to flag needs-attention in lists.
const STATUS_COLORS = {
    NEW:                            { bg: '#fee2e2',   text: '#dc2626', label: 'Not Booked' },
    CONSULTATION_BOOKED:            { bg: '#8E33FF29', text: '#5119B7', label: 'Consultation Booked' },
    CONSULTATION_ATTENDED:          { bg: '#FFC10729', text: '#B78103', label: 'Consultation Attended' },
    TREATMENT_STARTED:              { bg: '#54D62C29', text: '#229A16', label: 'Treatment Started' },
    FAILED_TO_ATTEND_CONSULTATION:  { bg: '#FFE5D0',   text: '#C2410C', label: 'Failed to Attend' }, // orange — distinct from Not Booked red
    CLOSED:                         { bg: '#919EAB29', text: '#637381', label: 'Completed Treatment' },
};

const TABLE_NAME = 'Reports Lead Table';
const ALL_COLUMNS = [
    { field: 'name', label: 'Name' },
    { field: 'treatment', label: 'Treatment' },
    { field: 'status', label: 'Status' },
    { field: 'value', label: 'Value' },
    { field: 'created_at', label: 'Date' },
    { field: 'source', label: 'Source' },
];

export default function ReportsLeadTable({
    practiceId,
    treatmentId,
    status,
    dateRange,
    onLeadClick,
}) {
    const [searchQuery, setSearchQuery] = useState('');
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [sortModel, setSortModel] = useState([{ field: 'created_at', sort: 'desc' }]);
    const [columnsAnchor, setColumnsAnchor] = useState(null);
    const [exportAnchor, setExportAnchor] = useState(null);
    const [hiddenColumns, setHiddenColumns] = useState([]);
    const [prefsLoaded, setPrefsLoaded] = useState(false);
    const [dateFormat, setDateFormat] = useState(() => localStorage.getItem('reportsDateFormat') || 'human_readable');
    const [dateFormatAnchor, setDateFormatAnchor] = useState(null);

    const { leads, reportsLoading } = useSelector((state) => state.analytics || {});
    const leadData = leads?.results || [];
    const totalCount = leads?.count || 0;

    // Table preferences from Redux
    const userId = useGetCurrentUserId();
    const buildExportName = useReportExportFilename();
    const { tablePreferences: tablePrefsRaw, globalTablePreferences } = useReduxSelector((state) => state.tablePreferences);
    const prefResults = tablePrefsRaw?.results || tablePrefsRaw || [];

    // Load user preferences on mount
    useEffect(() => {
        if (userId) {
            dispatch(getTablePreferences({ practiceUserId: userId }));
        }
    }, [userId]);

    // Save preferences to API
    const savePreferences = useCallback((newHidden) => {
        if (!userId) return;
        dispatch(setTablePreferenceLoading(true));
        const tableData = getCurrentTableData(prefResults, TABLE_NAME);
        const globalData = getCurrentTableData(globalTablePreferences?.results || [], TABLE_NAME);
        const currentTableData = isArray(tableData) && tableData.length > 0 ? tableData : globalData;
        const currentPreferences = currentTableData?.[0]?.preferences || {};
        const updatedPrefs = { ...currentPreferences, hiddenColumns: newHidden };
        const payload = {
            body: { table_name: TABLE_NAME, preferences: updatedPrefs },
            practiceUserId: userId,
        };
        if (isArray(tableData) && tableData.length > 0) {
            dispatch(handleTablePreferencesEdit({ ...payload, tableId: tableData[0].id }));
        } else {
            dispatch(handleTablePreferences(payload));
        }
    }, [userId, prefResults, globalTablePreferences]);

    // Load saved preferences
    useEffect(() => {
        if (!prefsLoaded && userId) {
            const tableData = getCurrentTableData(prefResults, TABLE_NAME);
            const globalData = getCurrentTableData(globalTablePreferences?.results || [], TABLE_NAME);
            const currentTableData = isArray(tableData) && tableData.length > 0 ? tableData : globalData;
            const prefs = currentTableData?.[0]?.preferences;
            if (prefs?.hiddenColumns && isArray(prefs.hiddenColumns)) {
                setHiddenColumns(prefs.hiddenColumns);
            }
            setPrefsLoaded(true);
        }
    }, [prefResults, globalTablePreferences, userId]);

    // Column visibility model for DataGrid
    const columnVisibilityModel = useMemo(() => {
        const model = {};
        hiddenColumns.forEach((field) => { model[field] = false; });
        return model;
    }, [hiddenColumns]);

    // Whether filter is active
    const isFilterActive = hiddenColumns.length > 0;

    // Toggle column visibility
    const handleToggleColumn = useCallback((field) => {
        const updated = hiddenColumns.includes(field)
            ? hiddenColumns.filter((f) => f !== field)
            : [...hiddenColumns, field];
        setHiddenColumns(updated);
        savePreferences(updated);
    }, [hiddenColumns, savePreferences]);

    // Fetch leads when filters change
    const fetchLeads = useCallback(() => {
        if (!practiceId) return;

        const orderBy = sortModel[0]?.field || 'created_at';
        const order = sortModel[0]?.sort || 'desc';

        dispatch(
            getLead({
                id: practiceId,
                treatment: treatmentId,
                status: status === 'All' ? null : status,
                limit: paginationModel.pageSize,
                offset: paginationModel.page * paginationModel.pageSize,
                order,
                orderId: orderBy,
                startDate: dateRange?.startDate,
                endDate: dateRange?.endDate,
                useCreatedAt: true,
            })
        );
    }, [practiceId, treatmentId, status, dateRange, paginationModel, sortModel]);

    useEffect(() => {
        fetchLeads();
    }, [fetchLeads]);

    // Get treatment name from lead data
    const getTreatmentName = (lead) => {
        const treatments = lead?.lead_treatments || [];
        if (treatments.length === 0) return '-';
        const names = treatments.map((t) => t?.treatment?.name || t?.treatment_name || 'Unknown').slice(0, 2);
        return names.length > 2 ? `${names.join(', ')} +${treatments.length - 2}` : names.join(', ');
    };

    // Get treatment value from lead data
    const getTreatmentValue = (lead) => {
        const treatments = lead?.lead_treatments || [];
        const total = treatments.reduce((sum, t) => sum + (t?.custom_price || 0), 0);
        return total > 0 ? `£${total.toLocaleString()}` : '-';
    };

    // Get lead status
    const getLeadStatus = (lead) => {
        const treatments = lead?.lead_treatments || [];
        if (treatments.length === 0) return 'NEW';
        return treatments[0]?.status || 'NEW';
    };

    // Get source from lead
    const getSource = (lead) => {
        const sourceType = lead?.lead_source?.type;
        const referral = lead?.lead_source?.referral;
        return referral || sourceType || '-';
    };

    // Parse date string — try ISO first, then DD-MM-YYYY as fallback
    const parseDate = (dateStr) => {
        if (!dateStr) return null;
        let d = dayjs(dateStr);
        if (!d.isValid() || /^\d{2}[-/]\d{2}[-/]\d{4}/.test(dateStr)) {
            const parsed = dayjs(dateStr, ['DD-MM-YYYY', 'DD/MM/YYYY', 'DD-MM-YYYY HH:mm:ss'], true);
            if (parsed.isValid()) d = parsed;
        }
        return d.isValid() ? d : null;
    };

    // Format date based on current dateFormat mode
    const formatDate = (dateStr, humanReadable) => {
        if (dateFormat === 'human_readable') {
            if (humanReadable) return humanReadable;
            const d = parseDate(dateStr);
            return d ? d.fromNow() : '-';
        }
        const d = parseDate(dateStr);
        if (!d) return '-';
        if (dateFormat === 'date_time_format') return d.format('ddd DD MMM YYYY, h:mm A');
        return d.format('ddd DD MMM YYYY');
    };

    // Handle date format toggle
    const handleDateFormatChange = (value) => {
        setDateFormat(value);
        localStorage.setItem('reportsDateFormat', value);
        setDateFormatAnchor(null);
    };

    // Export CSV
    const handleExportCSV = useCallback(() => {
        setExportAnchor(null);
        if (!filteredData || filteredData.length === 0) return;
        const visibleCols = ALL_COLUMNS.filter((c) => !hiddenColumns.includes(c.field));
        const headers = visibleCols.map((c) => c.label);
        const csvRows = filteredData.map((row) => visibleCols.map((col) => {
            if (col.field === 'created_at') return formatDate(row.created_at);
            if (col.field === 'status') {
                const statusConfig = STATUS_COLORS[row.status] || STATUS_COLORS.NEW;
                return statusConfig.label;
            }
            return row[col.field] ?? '-';
        }));
        const csvContent = [headers, ...csvRows].map((r) => r.map((cell) => `"${cell}"`).join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = buildExportName('leads-report', dateRange);
        link.click();
        URL.revokeObjectURL(link.href);
    }, [hiddenColumns, filteredData, formatDate]);

    // Process lead data with calculated fields
    const processedData = useMemo(() =>
        leadData.map((lead) => ({
            id: lead.id,
            name: `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'Unknown',
            firstName: lead.first_name,
            lastName: lead.last_name,
            treatment: getTreatmentName(lead),
            status: getLeadStatus(lead),
            value: getTreatmentValue(lead),
            created_at: lead.created_at,
            created_at_human: lead.created_at_human,
            source: getSource(lead),
            rawLead: lead, // Keep original for click handler
        })),
        [leadData]
    );

    // Filter by search query (client-side)
    const filteredData = useMemo(() => {
        if (!searchQuery) return processedData;
        const query = searchQuery.toLowerCase();
        return processedData.filter((row) =>
            row.name.toLowerCase().includes(query) ||
            row.treatment.toLowerCase().includes(query)
        );
    }, [processedData, searchQuery]);

    const columns = useMemo(() => [
        {
            field: 'name',
            headerName: 'Name',
            flex: 1.5,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pl: 1 }}>
                    <Avatar
                        sx={{
                            width: 32,
                            height: 32,
                            fontSize: '12px',
                            fontWeight: 600,
                            bgcolor: alpha('#3b82f6', 0.1),
                            color: '#3b82f6',
                        }}
                    >
                        {(params.row.firstName?.[0] || '').toUpperCase()}
                        {(params.row.lastName?.[0] || '').toUpperCase()}
                    </Avatar>
                    <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>
                        {params.value}
                    </Typography>
                </Box>
            ),
        },
        {
            field: 'treatment',
            headerName: 'Treatment',
            flex: 1.2,
            renderCell: (params) => (
                <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>
                    {params.value}
                </Typography>
            ),
        },
        {
            field: 'status',
            headerName: 'Status',
            flex: 1.2,
            renderCell: (params) => {
                const statusConfig = STATUS_COLORS[params.value] || STATUS_COLORS.NEW;
                return (
                    <Chip
                        label={statusConfig.label}
                        size="small"
                        sx={{
                            bgcolor: statusConfig.bg,
                            color: statusConfig.text,
                            fontWeight: 600,
                            fontSize: '11px',
                            height: 24,
                        }}
                    />
                );
            },
        },
        {
            field: 'value',
            headerName: 'Value',
            flex: 0.8,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#10b981' }}>
                    {params.value}
                </Typography>
            ),
        },
        {
            field: 'created_at',
            headerName: 'Date',
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => (
                <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
                    onClick={(e) => {
                        e.stopPropagation();
                        setDateFormatAnchor(e.currentTarget);
                    }}
                >
                    <Typography sx={{
                        fontWeight: 600,
                        fontSize: '12px',
                        color: '#64748b',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                    }}>
                        {DATE_TITLES.find((o) => o.value === dateFormat)?.label || 'Date'}
                    </Typography>
                    <Iconify
                        icon="mdi:calendar-filter-outline"
                        width={15}
                        height={15}
                        sx={{ color: '#64748b' }}
                    />
                </Box>
            ),
            renderCell: (params) => (
                <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                    {formatDate(params.row.created_at, params.row.created_at_human)}
                </Typography>
            ),
        },
        {
            field: 'source',
            headerName: 'Source',
            flex: 1,
            renderCell: (params) => (
                <Box sx={{ pr: 2 }}>
                    <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                        {params.value}
                    </Typography>
                </Box>
            ),
        },
    ], [dateFormat]);

    const handleRowClick = (params) => {
        if (onLeadClick) {
            onLeadClick(params.row.rawLead);
        }
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Box
                sx={{
                    bgcolor: '#fff',
                    borderRadius: '16px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    overflow: 'hidden',
                }}
            >
                {/* Header with Search and Toolbar */}
                <Box
                    sx={{
                        p: 2.5,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 2,
                    }}
                >
                    <Tooltip title="All leads with details. Search and sort to find specific leads." placement="bottom" arrow
                        slotProps={{ tooltip: { sx: { bgcolor: '#1e293b', fontSize: '12px', maxWidth: 280 } } }}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
                            <Typography sx={{ fontSize: '20px', fontWeight: 700 }}>
                                Leads
                            </Typography>
                            <Iconify icon="mdi:information-outline" width={14} sx={{ color: '#94a3b8' }} />
                        </Box>
                    </Tooltip>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <TextField
                            size="small"
                            placeholder="Search by name or treatment..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                width: { xs: '100%', sm: 300 },
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                },
                            }}
                        />

                        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                        {/* Column visibility */}
                        <Tooltip title="Show/hide columns" arrow>
                            <Badge
                                variant="dot"
                                invisible={!isFilterActive}
                                sx={{
                                    '& .MuiBadge-badge': {
                                        bgcolor: '#3b82f6',
                                        width: 8,
                                        height: 8,
                                        minWidth: 8,
                                        top: 4,
                                        right: 4,
                                    },
                                }}
                            >
                                <IconButton
                                    size="small"
                                    onClick={(e) => setColumnsAnchor(e.currentTarget)}
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        border: '1px solid',
                                        borderColor: (isFilterActive || columnsAnchor) ? 'primary.main' : '#e2e8f0',
                                        borderRadius: '8px',
                                        bgcolor: columnsAnchor ? alpha('#3b82f6', 0.08) : 'transparent',
                                        '&:hover': { bgcolor: alpha('#3b82f6', 0.08) },
                                    }}
                                >
                                    <Iconify icon="eva:options-2-outline" width={16} sx={{ color: (isFilterActive || columnsAnchor) ? 'primary.main' : '#64748b' }} />
                                </IconButton>
                            </Badge>
                        </Tooltip>

                        {/* Export */}
                        <Tooltip title="Export data" arrow>
                            <IconButton
                                size="small"
                                onClick={(e) => setExportAnchor(e.currentTarget)}
                                sx={{
                                    width: 32,
                                    height: 32,
                                    border: '1px solid',
                                    borderColor: exportAnchor ? 'primary.main' : '#e2e8f0',
                                    borderRadius: '8px',
                                    bgcolor: exportAnchor ? alpha('#3b82f6', 0.08) : 'transparent',
                                    '&:hover': { bgcolor: alpha('#3b82f6', 0.08) },
                                }}
                            >
                                <Iconify icon="eva:download-outline" width={16} sx={{ color: exportAnchor ? 'primary.main' : '#64748b' }} />
                            </IconButton>
                        </Tooltip>

                        {/* Column visibility popover */}
                        <Popover
                            open={Boolean(columnsAnchor)}
                            anchorEl={columnsAnchor}
                            onClose={() => setColumnsAnchor(null)}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            slotProps={{ paper: { sx: { mt: 1, borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', width: 240, maxHeight: 400 } } }}
                        >
                            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f1f5f9' }}>
                                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                                    Columns
                                </Typography>
                            </Box>
                            <List dense sx={{ py: 0.5 }}>
                                {ALL_COLUMNS.map((col) => {
                                    const isVisible = !hiddenColumns.includes(col.field);
                                    return (
                                        <ListItemButton
                                            key={col.field}
                                            onClick={() => handleToggleColumn(col.field)}
                                            sx={{ py: 0.25, px: 1.5, opacity: isVisible ? 1 : 0.6 }}
                                        >
                                            <Checkbox
                                                size="small"
                                                checked={isVisible}
                                                sx={{ p: 0, mr: 1 }}
                                            />
                                            <ListItemText
                                                primary={col.label}
                                                primaryTypographyProps={{ fontSize: '13px', color: isVisible ? '#1e293b' : '#94a3b8' }}
                                            />
                                        </ListItemButton>
                                    );
                                })}
                            </List>
                        </Popover>

                        {/* Export popover */}
                        <Popover
                            open={Boolean(exportAnchor)}
                            anchorEl={exportAnchor}
                            onClose={() => setExportAnchor(null)}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            slotProps={{ paper: { sx: { mt: 1, borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', minWidth: 200 } } }}
                        >
                            <List dense sx={{ py: 0.5 }}>
                                <ListItemButton onClick={handleExportCSV} sx={{ py: 1, px: 2 }}>
                                    <Iconify icon="mdi:file-delimited-outline" width={18} sx={{ color: '#64748b', mr: 1.5 }} />
                                    <ListItemText primary="Export CSV" primaryTypographyProps={{ fontSize: '13px' }} />
                                </ListItemButton>
                            </List>
                        </Popover>
                    </Box>
                </Box>

                {/* Date format popover */}
                <Popover
                    open={Boolean(dateFormatAnchor)}
                    anchorEl={dateFormatAnchor}
                    onClose={() => setDateFormatAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    slotProps={{
                        paper: {
                            sx: {
                                mt: 0.5,
                                borderRadius: '12px',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                                minWidth: 140,
                            },
                        },
                    }}
                >
                    <List dense sx={{ py: 0.5 }}>
                        {DATE_TITLES.map((option) => (
                            <ListItemButton
                                key={option.value}
                                onClick={() => handleDateFormatChange(option.value)}
                                selected={dateFormat === option.value}
                                sx={{ py: 1, px: 2 }}
                            >
                                <Iconify
                                    icon={option.icon}
                                    width={18}
                                    height={18}
                                    sx={{ mr: 1.5, color: dateFormat === option.value ? 'primary.main' : '#64748b' }}
                                />
                                <ListItemText
                                    primary={option.label}
                                    primaryTypographyProps={{
                                        fontSize: '13px',
                                        fontWeight: dateFormat === option.value ? 600 : 400,
                                    }}
                                />
                            </ListItemButton>
                        ))}
                    </List>
                </Popover>

                {/* Loading or DataGrid */}
                {reportsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box>
                        <DataGridPro
                            rows={filteredData}
                            columns={columns}
                            rowCount={totalCount}
                            paginationMode="server"
                            paginationModel={paginationModel}
                            onPaginationModelChange={setPaginationModel}
                            sortModel={sortModel}
                            onSortModelChange={setSortModel}
                            sortingMode="server"
                            onRowClick={handleRowClick}
                            columnVisibilityModel={columnVisibilityModel}
                            autoHeight
                            disableRowSelectionOnClick
                            disableColumnMenu={false}
                            pageSizeOptions={[10, 25, 50]}
                            rowHeight={60}
                            sx={{
                                border: 'none',
                                cursor: onLeadClick ? 'pointer' : 'default',
                                '& .MuiDataGrid-columnHeaders': {
                                    bgcolor: '#f8fafc',
                                    borderBottom: '1px solid #e2e8f0',
                                },
                                '& .MuiDataGrid-columnHeaderTitle': {
                                    fontWeight: 600,
                                    fontSize: '12px',
                                    color: '#64748b',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                },
                                '& .MuiDataGrid-cell': {
                                    borderBottom: '1px solid #f1f5f9',
                                    py: 1,
                                },
                                '& .MuiDataGrid-row:hover': {
                                    bgcolor: alpha('#3b82f6', 0.04),
                                },
                            }}
                        />
                    </Box>
                )}
            </Box>
        </Box>
    );
}

ReportsLeadTable.propTypes = {
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    treatmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    status: PropTypes.string,
    dateRange: PropTypes.shape({
        startDate: PropTypes.string,
        endDate: PropTypes.string,
    }),
    onLeadClick: PropTypes.func,
};

ReportsLeadTable.defaultProps = {
    practiceId: null,
    treatmentId: null,
    status: 'All',
    dateRange: null,
    onLeadClick: null,
};
